import { orderBy } from "lodash";
import moment from "moment";
import { BerlinDate, DateTime, GermanTimezone, UTCDate } from "../date";

export function getNoBookingPriorDate(): BerlinDate {
  return BerlinDate.fromUTCDate(UTCDate.fromYMD(2025, 3, 1));
}

export function getNoInvoicePriorDate(): BerlinDate {
  return BerlinDate.fromUTCDate(UTCDate.fromYMD(2025, 3, 1));
}

export enum PaymentPeriod {
  M1 = "M1",
  M3 = "M3",
  M6 = "M6",
  M12 = "M12",
  Quarterly = "Quarterly",
}

export enum PaymentTime {
  InArrears = "InArrears",
  InAdvance = "InAdvance",
}

export interface PaymentPeriodDetail {
  paymentDate: BerlinDate;
  percentage: number;
  periodStart: BerlinDate;
  periodEnd: BerlinDate;
  numberOfMonths: number;
}

interface DayOfYear {
  day: number;
  month: number;
}

interface ITimespan {
  months?: number;
  days?: number;
}

type PaymentTranche =
  | {
      type: "dayOfYear";
      percentage: number;
      dueDate: DayOfYear;
    }
  | {
      type: "relative";
      percentage: number;
      dueDate: ITimespan;
    }
  | {
      type: string;
      percentage: number;
      dueDate: any;
    };

export interface PaymentSettings {
  period: PaymentPeriod;
  paymentTime: PaymentTime;
  startDate: UTCDate;
  paymentTranches?: PaymentTranche[];
}

export interface BillingYearBerlin {
  startDate: BerlinDate;
  endDate: BerlinDate;
  periods: BillingPeriodBerlin[];
}

export interface BillingPeriodBerlin {
  startDate: BerlinDate;
  endDate: BerlinDate;
  isFirstPeriod: boolean;
  isLastPeriod: boolean;
}

export interface BillingYearUtc {
  startDate: UTCDate;
  endDate: UTCDate;
  periods: BillingPeriodUtc[];
}

export interface BillingPeriodUtc {
  startDate: UTCDate;
  endDate: UTCDate;
  isFirstPeriod: boolean;
  isLastPeriod: boolean;
}

/*
General remark, when working with dates:
- Always use BerlinDateOnly to avoid timezone issues.
- BerlinDateOnly always uses midnight in the german timezone as time to avoid time issues.
- NEVER use JS Date object directly, always use moment.js to work with dates!

JS Date object is a mess! DO NOT USE IT!
E.g.
let d = new Date("2022-01-01T00:00:00.000Z");
d.setMonth(d.getMonth() + 4)
results in "2022-04-30T23:00:00Z"
instead of "2022-05-01T00:00:00.000Z"

Also keep in mind:
- month is zero-based in both - JS Date and moment.js!
- date is one-based in both!
- always clone moment.js objects before modifying them!
*/

// #region (public) "JS Date"

export function rollDate(date: BerlinDate, tenor: string, rollDay: number): BerlinDate {
  const moment = parseAndValidateDate(date);
  return BerlinDate.fromDate(rollMoment(moment, tenor, rollDay).toDate());
}

export function getBillingYears(
  from: BerlinDate,
  to: BerlinDate,
  paymentSettings: PaymentSettings[]
): BillingYearBerlin[] {
  const billingYears: BillingYearBerlin[] = [];
  let dateInYear = from;
  const endDate = to ?? DateTime.now();
  while (dateInYear.getTime() < endDate.getTime()) {
    const billingYear = getBillingYear(paymentSettings, dateInYear);
    billingYears.push(billingYear);

    dateInYear = BerlinDate.fromYMD(dateInYear.getFullYear() + 1, dateInYear.getMonth(), dateInYear.getDate());

    if (dateInYear.getTime() > endDate.getTime()) {
      dateInYear = endDate instanceof DateTime ? BerlinDate.fromUTCDate(endDate.toUTCDate(GermanTimezone)) : endDate;
    }
  }
  return billingYears;
}

export function getBillingYear(
  paymentSettings: PaymentSettings[],
  anyDateInPaymentYear: BerlinDate | DateTime
): BillingYearBerlin {
  anyDateInPaymentYear =
    anyDateInPaymentYear instanceof BerlinDate
      ? anyDateInPaymentYear
      : anyDateInPaymentYear
        ? BerlinDate.fromUTCDate(anyDateInPaymentYear.toUTCDate(GermanTimezone))
        : null;

  if (!paymentSettings?.length || paymentSettings.some((x) => !x)) {
    throw new Error("Payment settings are required.");
  }

  if (!anyDateInPaymentYear) {
    throw new Error("Any date in payment year is required.");
  }

  const activePaymentSettings = getActivePaymentSetting(paymentSettings, anyDateInPaymentYear);

  if (!activePaymentSettings) {
    throw new Error("Active payment settings are required.");
  }

  const paymentStart = parseAndValidateDate(BerlinDate.fromUTCDate(activePaymentSettings.startDate));
  const anyMomentInPaymentYear = moment.tz(anyDateInPaymentYear.toDate(), GermanTimezone);
  const periods = getBillingPeriods(paymentStart, activePaymentSettings.period, anyMomentInPaymentYear);

  const billingYear: BillingYearBerlin = {
    startDate: periods[0].startDate,
    endDate: periods[periods.length - 1].endDate,
    periods: periods,
  };

  return billingYear;
}

export function getActivePaymentSetting(
  paymentSettings: PaymentSettings[],
  anyDateInPaymentYear: BerlinDate | DateTime
): PaymentSettings {
  if (anyDateInPaymentYear instanceof DateTime) {
    anyDateInPaymentYear =
      anyDateInPaymentYear instanceof BerlinDate
        ? anyDateInPaymentYear
        : BerlinDate.fromUTCDate(anyDateInPaymentYear.toUTCDate(GermanTimezone));
  }

  return orderBy(paymentSettings, (x) => x.startDate, "desc").find(
    (x) => BerlinDate.fromUTCDate(x.startDate).getTime() <= anyDateInPaymentYear.getTime()
  );
}

export function getBillingPeriodsForOneYear(
  paymentStart: BerlinDate,
  anyDateInPaymentYear: BerlinDate,
  paymentPeriod: PaymentPeriod
): BillingPeriodBerlin[] {
  const paymentStartMoment = parseAndValidateDate(paymentStart);
  const anyMomentInPaymentYear = moment.utc(anyDateInPaymentYear.toDate());
  return getBillingPeriodsForOneYearUsingMoment(paymentStartMoment, paymentPeriod, anyMomentInPaymentYear);
}

export function isInBillingPeriod(
  date: BerlinDate | DateTime,
  billingPeriod: BillingPeriodBerlin | { startDate: BerlinDate; endDate: BerlinDate }
): boolean {
  const moment = parseAndValidateDate(date);
  return moment.isBetween(billingPeriod.startDate.toDate(), billingPeriod.endDate.toDate(), "day", "[]");
}

export function isInBillingYear(date: BerlinDate, billingYear: BillingYearBerlin): boolean {
  const moment = parseAndValidateDate(date);
  return moment.isBetween(billingYear.startDate.toDate(), billingYear.endDate.toDate(), "day", "[]");
}

// #endregion

function parseAndValidateDate(date: BerlinDate | DateTime): moment.Moment {
  if (date instanceof DateTime) {
    return moment.tz(date.toDate(), GermanTimezone);
  }

  return moment.tz(date.toDate(), GermanTimezone);
}

export function mapBillingPeriodToBerlinDate(date: BillingPeriodUtc): BillingPeriodBerlin {
  return {
    startDate: BerlinDate.fromUTCDate(date.startDate),
    endDate: BerlinDate.fromUTCDate(date.endDate),
    isFirstPeriod: date.isFirstPeriod,
    isLastPeriod: date.isLastPeriod,
  };
}

export function mapBillingPeriodToUtcDate(date: BillingPeriodBerlin): BillingPeriodUtc {
  return {
    startDate: date.startDate.toUTCDate(),
    endDate: date.endDate.toUTCDate(),
    isFirstPeriod: date.isFirstPeriod,
    isLastPeriod: date.isLastPeriod,
  };
}

export function mapBillingYearToBerlinDate(date: BillingYearUtc): BillingYearBerlin {
  return {
    startDate: BerlinDate.fromUTCDate(date.startDate),
    endDate: BerlinDate.fromUTCDate(date.endDate),
    periods: date.periods.map(mapBillingPeriodToBerlinDate),
  };
}

export function mapBillingYearToUtcDate(date: BillingYearBerlin): BillingYearUtc {
  return {
    startDate: date.startDate.toUTCDate(),
    endDate: date.endDate.toUTCDate(),
    periods: date.periods.map(mapBillingPeriodToUtcDate),
  };
}

// #region (private) "moment.js"

function getBillingPeriods(
  paymentStart: moment.Moment,
  paymentPeriod: PaymentPeriod,
  anyMomentInPaymentYear: moment.Moment
): BillingPeriodBerlin[] {
  if (paymentPeriod === "Quarterly") {
    const anyYear = anyMomentInPaymentYear.year();
    return getQuarterlyBillingPeriods(paymentStart, anyYear);
  } else {
    return getBillingPeriodsForOneYearUsingMoment(paymentStart, paymentPeriod, anyMomentInPaymentYear);
  }
}

function getQuarterlyBillingPeriods(paymentStart: moment.Moment, year: number): BillingPeriodBerlin[] {
  const periods: BillingPeriodBerlin[] = [];
  const startDate = paymentStart.clone();
  if (startDate.year() < year) {
    // even with moment.js, month is zero-based and date is one-based
    // In JS date smells really funny!
    startDate.set("year", year).set("month", 0).set("date", 1);
  }
  let periodStartDate = startDate.clone();
  while (periodStartDate.year() < year + 1) {
    const endOfQuarter = getEndOfQuarter(periodStartDate);
    const periodEndDate = endOfQuarter === periodStartDate ? rollMoment(endOfQuarter, "M3", 31) : endOfQuarter.clone();
    periods.push({
      startDate: BerlinDate.fromDate(periodStartDate.toDate()),
      endDate: BerlinDate.fromDate(periodEndDate.toDate()),
      isFirstPeriod: false,
      isLastPeriod: false,
    });
    periodStartDate = periodEndDate.clone().add(1, "day");
  }
  markFirstAndLastPeriod(periods);
  return periods;
}

function getEndOfQuarter(aMoment: moment.Moment): moment.Moment {
  // keep in mind that month is zero-based!
  const quarter = Math.ceil((aMoment.month() + 1) / 3);
  return aMoment
    .clone()
    .month(quarter * 3 - 1)
    .endOf("month")
    .startOf("day");
}

function getBillingPeriodsForOneYearUsingMoment(
  paymentStart: moment.Moment,
  paymentPeriod: PaymentPeriod,
  anyMomentInPaymentYear: moment.Moment
): BillingPeriodBerlin[] {
  const anyYear = anyMomentInPaymentYear.year();
  const startDate = paymentStart.clone().set("year", anyYear);
  if (startDate > anyMomentInPaymentYear) {
    startDate.set("year", anyYear - 1);
  }

  const tenorInMonths = getTenorInMonths(paymentPeriod);
  const numberOfPeriods = 12 / tenorInMonths;
  const rollDay = paymentStart.date();
  let periodStartDate = startDate.clone();

  const periods: BillingPeriodBerlin[] = [];
  for (let i = 0; i < numberOfPeriods; i++) {
    const rollDate = rollMoment(periodStartDate, paymentPeriod, rollDay);
    const periodEndDate = rollDate.clone().subtract(1, "day");
    periods.push({
      startDate: BerlinDate.fromDate(periodStartDate.toDate()),
      endDate: BerlinDate.fromDate(periodEndDate.toDate()),
      isFirstPeriod: false,
      isLastPeriod: false,
    });
    periodStartDate = rollDate.clone();
  }
  markFirstAndLastPeriod(periods);

  return periods;
}

function markFirstAndLastPeriod(periods: BillingPeriodBerlin[]): void {
  periods[0].isFirstPeriod = true;
  periods[periods.length - 1].isLastPeriod = true;
}

function rollMoment(moment: moment.Moment, tenor: string, rollDay: number): moment.Moment {
  const newMoment = moment.clone();
  const tenorInMonths = getTenorInMonths(tenor);
  newMoment.add(tenorInMonths, "months");
  const rollDayToSet = Math.min(rollDay, newMoment.daysInMonth());
  newMoment.set("date", rollDayToSet);
  return newMoment;
}

function getTenorInMonths(tenor: string): number {
  switch (tenor) {
    case PaymentPeriod.M1:
      return 1;
    case PaymentPeriod.M3:
      return 3;
    case PaymentPeriod.M6:
      return 6;
    case PaymentPeriod.M12:
      return 12;
    default:
      throw new Error(`Invalid tenor: ${tenor}`);
  }
}

//#endregion

// #region payment settings
export function getPaymentDates(
  paymentSettings: PaymentSettings[],
  anyDateInPaymentYear: BerlinDate
): PaymentPeriodDetail[] {
  const activePaymentSettings = getActivePaymentSetting(paymentSettings, anyDateInPaymentYear);
  if (!activePaymentSettings) {
    throw new Error("Active payment settings are required.");
  }
  const billingYear = getBillingYear(paymentSettings, anyDateInPaymentYear);
  if (activePaymentSettings.paymentTime === PaymentTime.InAdvance) {
    return getPaymentDatesInAdvance(activePaymentSettings, billingYear);
  } else {
    return getPaymentDatesInArrears(activePaymentSettings, billingYear);
  }
}

function getPaymentDatesInAdvance(
  paymentSettings: PaymentSettings,
  billingYear: BillingYearBerlin
): PaymentPeriodDetail[] {
  if (paymentSettings.paymentTranches?.length) {
    return paymentSettings.paymentTranches.map((tranche) => ({
      paymentDate: getPaymentDateForTranche(tranche, billingYear, BerlinDate.fromUTCDate(paymentSettings.startDate)),
      percentage: tranche.percentage,
      periodStart: billingYear.startDate,
      periodEnd: billingYear.endDate,
      numberOfMonths: 12,
    }));
  }

  return billingYear.periods.map((period) => ({
    paymentDate: period.startDate,
    percentage: 100,
    periodStart: period.startDate,
    periodEnd: period.endDate,
    numberOfMonths: getTenorInMonths(paymentSettings.period),
  }));
}

function getPaymentDatesInArrears(
  paymentSettings: PaymentSettings,
  billingYear: BillingYearBerlin
): PaymentPeriodDetail[] {
  if (paymentSettings.paymentTranches?.length) {
    throw new Error("Payment tranches are not supported for payment time in arrears.");
  }
  const numberOfMonths = getTenorInMonths(paymentSettings.period);
  return billingYear.periods.map((period) => ({
    paymentDate: period.endDate,
    percentage: 100,
    periodStart: period.startDate,
    periodEnd: period.endDate,
    numberOfMonths: numberOfMonths,
  }));
}

function getPaymentDateForTranche(
  tranche: PaymentTranche,
  billingYear: BillingYearBerlin,
  referenceDate: BerlinDate
): BerlinDate {
  if (tranche.type === "dayOfYear") {
    let date = billingYear.startDate;
    date = date.setMonth(tranche.dueDate.month);
    date = date.setDayOfMonth(tranche.dueDate.day);

    if (date.getTime() < billingYear.startDate.getTime()) {
      date = BerlinDate.fromYMD(date.getFullYear() + 1, date.getMonth(), date.getDate());
    }

    return date;
  }

  if (tranche.type === "relative") {
    const paymentDateMoment = moment.tz(referenceDate.toDate(), GermanTimezone);
    paymentDateMoment.set("year", billingYear.startDate.getFullYear() - 1);
    paymentDateMoment.add(tranche.dueDate.months ?? 0, "months");
    paymentDateMoment.add(tranche.dueDate.days ?? 0, "days");

    let date = BerlinDate.fromDate(paymentDateMoment.toDate());

    if (date.getTime() < billingYear.startDate.getTime()) {
      date = BerlinDate.fromYMD(date.getFullYear() + 1, date.getMonth(), date.getDate());
    }

    return date;
  }

  throw new Error("Invalid tranche type");
}

// #endregion
